import { t } from 'i18next';
import { AnyAction } from 'redux';
import { put, call, takeLatest, retry } from '@redux-saga/core/effects';
import { error, success } from '@innovyze/stylovyze';
import { getTenantId } from '@innovyze/lib_get_service';
import {
	upsertFailureDefinition,
	upsertFailureDefinitionResolved,
	upsertFailureDefinitionRejected,
	runFailureDefinition,
	runFailureDefinitionResolved,
	runFailureDefinitionRejected,
	listAllFailureDefinitionsResolved,
	listAllFailureDefinitions,
	listAllFailureDefinitionsRejected,
	getFailureDefinitionResolved,
	getFailureDefinitionRejected,
	getFailureDefinitionAction,
	getFailureDefinitionRunResults,
	getFailureDefinitionRunResultsResolved,
	getFailureDefinitionRunResultsRejected,
	deleteFailureDefinition,
	deleteFailureDefinitionResolved,
	deleteFailureDefinitionRejected,
	copyFailureDefinition,
	copyFailureDefinitionResolved,
	copyFailureDefinitionRejected,
	updateFailureDefinitionPublishedStatus,
	updateFailureDefinitionPublishedStatusResolved,
	updateFailureDefinitionPublishedStatusRejected,
	renameFailureDefinition,
	renameFailureDefinitionResolved,
	renameFailureDefinitionRejected,
} from '@Actions/failureDefinition.actions';
import {
	getFailureDefinition,
	getFailureDefinitions,
	upsertFailureDefinition as upsertFailureDefinitionService,
	runFailureDefinition as runFailureDefinitionService,
	getFailureDefinitionRunResults as getFailureDefinitionRunResultsService,
	deleteFailureDefinition as deleteFailureDefinitionService,
	copyFailureDefinition as copyFailureDefinitionService,
	updateFailureDefinition as updateFailureDefinitionService,
} from '@Services/failureDefinition.services';
import { getApiErrorMessage, isDuplicateNameError } from '@Utils/apiError';
import { AxiosResponse } from 'axios';
import {
	GetAllFailureDefinitionsResponse,
	FailureDefinition,
	GetFailureDefinitionResponse,
	GetFailureDefinitionRunResultsResponse,
	FailureDefinitionRunResult,
	failureDefinitionStatusToStatusType,
} from '@Types';

export function* upsertFailureDefinitionSaga(action: AnyAction) {
	const { request, onSuccess, onError } = action.payload;
	try {
		const tenantId = getTenantId() || '';
		const isUpdate = !!request.failure_definition_id;

		const response: AxiosResponse<{ failure_definition_id: string }> = yield call(
			upsertFailureDefinitionService,
			request,
			tenantId,
		);
		const resultId = response.data.failure_definition_id;

		yield put(upsertFailureDefinitionResolved({ id: resultId }));
		yield put(
			success(t(isUpdate ? 'Failure definition updated successfully' : 'Failure definition created successfully')),
		);

		yield put(runFailureDefinition({ id: resultId }));

		if (onSuccess) {
			onSuccess(resultId);
		}
	} catch (e) {
		console.error('Error upserting failure definition:', e);
		yield put(upsertFailureDefinitionRejected());
		const message = getApiErrorMessage(e, t('Unable to save failure definition'));
		if (isDuplicateNameError(e)) {
			if (onError) {
				onError(message);
			}
		} else {
			yield put(error(message));
		}
	}
}

export function* runFailureDefinitionSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId() || '';
		yield call(runFailureDefinitionService, action.payload.id, tenantId);
		yield put(runFailureDefinitionResolved({ id: action.payload.id }));
		yield put(success(t('Failure definition run started successfully')));
	} catch (e) {
		console.error('Error running failure definition:', e);
		yield put(
			runFailureDefinitionRejected({
				code: -1,
				text: t('Unable to run failure definition'),
			}),
		);
		yield put(error(t('Unable to run failure definition')));
	}
}

export function* deleteFailureDefinitionSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId() || '';
		yield call(deleteFailureDefinitionService, action.payload.id, tenantId);
		yield put(deleteFailureDefinitionResolved({ id: action.payload.id }));
		yield put(success(t('Failure definition deleted successfully')));

		yield put(listAllFailureDefinitions());
	} catch (e) {
		console.error('Error deleting failure definition:', e);
		yield put(
			deleteFailureDefinitionRejected({
				code: -1,
				text: t('Unable to delete failure definition'),
			}),
		);
		yield put(error(t('Unable to delete failure definition')));
	}
}

export function* copyFailureDefinitionDataSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId() || '';
		const { id, onSuccess } = action.payload;
		const response: AxiosResponse<{ failure_definition_id: string; message: string }> = yield call(
			copyFailureDefinitionService,
			id,
			tenantId,
		);
		const newId = response.data.failure_definition_id;

		yield put(copyFailureDefinitionResolved({ id: newId }));
		yield put(success(t('Failure definition copied successfully')));

		yield put(listAllFailureDefinitions());

		if (onSuccess) {
			onSuccess(newId);
		}
	} catch (e) {
		console.error('Error copying failure definition:', e);
		yield put(
			copyFailureDefinitionRejected({
				code: -1,
				text: t('Unable to copy failure definition'),
			}),
		);
		yield put(error(t('Unable to copy failure definition')));

		const { onError } = action.payload;
		if (onError) {
			onError();
		}
	}
}

function* watchUpsertFailureDefinitionSaga() {
	yield takeLatest(upsertFailureDefinition, upsertFailureDefinitionSaga);
}

function* watchRunFailureDefinitionSaga() {
	yield takeLatest(runFailureDefinition, runFailureDefinitionSaga);
}

function* watchDeleteFailureDefinitionSaga() {
	yield takeLatest(deleteFailureDefinition, deleteFailureDefinitionSaga);
}

function* watchCopyFailureDefinitionSaga() {
	yield takeLatest(copyFailureDefinition, copyFailureDefinitionDataSaga);
}

export function* listAllFailureDefinitionsSaga() {
	try {
		const tenantId = getTenantId() || '';
		const payload: AxiosResponse<GetAllFailureDefinitionsResponse> = yield retry(
			5,
			15000,
			getFailureDefinitions,
			tenantId,
		);

		const transformedFailureDefinitions: FailureDefinition[] = payload.data.failure_definitions.map(fd => ({
			id: fd.id,
			name: fd.name,
			systemType: fd.system_type,
			assetType: fd.asset_type,
			inspectionStandard: fd.inspection_standard,
			lastRunId: fd.last_run_id,
			lastRunAt: fd.last_run_at,
			status: failureDefinitionStatusToStatusType(fd.status),
			runBy: fd.last_run_by,
			createdAt: fd.created_at,
			updatedAt: fd.updated_at,
			assetCount: fd.asset_count,
			events: fd.events,
			inspections: fd.inspections,
			tasks: fd.tasks,
			prioritizedCategory: fd.prioritized_category,
			published: fd.published,
		}));

		yield put(listAllFailureDefinitionsResolved(transformedFailureDefinitions));
	} catch (e) {
		console.error('Error retrieving failure definitions:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			listAllFailureDefinitionsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to list failure definitions')));
	}
}

function* watchListAllFailureDefinitionsSaga() {
	yield takeLatest(listAllFailureDefinitions, listAllFailureDefinitionsSaga);
}

export function* getFailureDefinitionSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId() || '';
		const payload: AxiosResponse<GetFailureDefinitionResponse> = yield retry(
			5,
			15000,
			getFailureDefinition,
			action.payload,
			tenantId,
		);
		const data = payload.data.failure_definition;
		const transformedFailureDefinition: FailureDefinition = {
			id: data.id,
			name: data.name,
			systemType: data.system_type,
			assetType: data.asset_type,
			inspectionStandard: data.inspection_standard,
			lastRunId: data.last_run_id,
			events: data.events,
			inspections: data.inspections,
			tasks: data.tasks,
			prioritizedCategory: data.prioritized_category,
			status: failureDefinitionStatusToStatusType(data.status),
			lastRunAt: data.last_run_at,
			createdAt: data.created_at,
			updatedAt: data.updated_at,
			assetCount: data.asset_count,
			runBy: data.last_run_by,
			published: data.published,
		};
		yield put(getFailureDefinitionResolved(transformedFailureDefinition));
	} catch (e) {
		console.error('Error retrieving failure definition:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getFailureDefinitionRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get failure definition')));
	}
}

function* watchGetFailureDefinitionSaga() {
	yield takeLatest(getFailureDefinitionAction, getFailureDefinitionSaga);
}

export function* getFailureDefinitionRunResultsSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId();
		if (!tenantId) {
			yield put(
				getFailureDefinitionRunResultsRejected({
					code: -1,
					text: t('Authentication error: Tenant ID not available'),
				}),
			);
			yield put(error(t('Authentication error: Please log in again')));
			return;
		}

		const request = action.payload;
		const payload: AxiosResponse<GetFailureDefinitionRunResultsResponse> = yield retry(
			5,
			15000,
			getFailureDefinitionRunResultsService,
			request,
			tenantId,
		);

		const transformedResults: FailureDefinitionRunResult[] = payload.data.data.map(apiResult => ({
			id: apiResult.id,
			tenantId: apiResult.tenant_id,
			assetId: apiResult.asset_id,
			runId: apiResult.run_id,
			queryId: apiResult.query_id,
			runBy: apiResult.run_by,
			completedAt: apiResult.completed_at,
			failureType: apiResult.failure_type,
			failureDetail: apiResult.failure_detail,
			failureDate: apiResult.failure_date,
			installationDate: apiResult.installation_date,
			ageAtFailure: apiResult.age_at_failure,
			allFailures: apiResult.all_failures.map(failure => ({
				failureType: failure.failure_type,
				failureDetail: failure.failure_detail,
				failureDate: failure.failure_date,
			})),
		}));

		yield put(
			getFailureDefinitionRunResultsResolved({
				data: transformedResults,
				pagination: payload.data.pagination,
			}),
		);
	} catch (e) {
		console.error('Error retrieving failure definition run results:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getFailureDefinitionRunResultsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get failure definition run results')));
	}
}

function* watchGetFailureDefinitionRunResultsSaga() {
	yield takeLatest(getFailureDefinitionRunResults, getFailureDefinitionRunResultsSaga);
}

export function* updateFailureDefinitionPublishedStatusSaga(action: AnyAction) {
	try {
		const tenantId = getTenantId() || '';
		const { failureDefinition, published } = action.payload;

		yield call(updateFailureDefinitionService, failureDefinition.id, tenantId, { published });

		yield put(
			updateFailureDefinitionPublishedStatusResolved({
				id: failureDefinition.id,
				published: published,
			}),
		);
		yield put(
			success(
				t(published ? 'Failure definition published successfully' : 'Failure definition unpublished successfully'),
			),
		);

		yield put(listAllFailureDefinitions());
	} catch (e) {
		console.error('Error updating failure definition published status:', e);
		yield put(
			updateFailureDefinitionPublishedStatusRejected({
				code: -1,
				text: t('Unable to update published status'),
			}),
		);
		yield put(error(t('Unable to update published status')));
	}
}

function* watchUpdateFailureDefinitionPublishedStatusSaga() {
	yield takeLatest(updateFailureDefinitionPublishedStatus, updateFailureDefinitionPublishedStatusSaga);
}

export function* renameFailureDefinitionSaga(action: AnyAction) {
	const { id, newName, onSuccess, onError } = action.payload;
	try {
		const tenantId = getTenantId() || '';

		yield call(updateFailureDefinitionService, id, tenantId, { name: newName });

		yield put(renameFailureDefinitionResolved({ id, name: newName }));
		yield put(success(t('Failure definition renamed successfully')));

		yield put(listAllFailureDefinitions());

		if (onSuccess) {
			onSuccess();
		}
	} catch (e) {
		console.error('Error renaming failure definition:', e);
		yield put(
			renameFailureDefinitionRejected({
				code: -1,
				text: t('Unable to rename failure definition'),
			}),
		);
		const message = getApiErrorMessage(e, t('Unable to rename failure definition'));
		if (isDuplicateNameError(e)) {
			if (onError) {
				onError(message);
			}
		} else {
			yield put(error(message));
		}
	}
}

function* watchRenameFailureDefinitionSaga() {
	yield takeLatest(renameFailureDefinition, renameFailureDefinitionSaga);
}

const failureDefinitionSagas = [
	watchUpsertFailureDefinitionSaga(),
	watchRunFailureDefinitionSaga(),
	watchDeleteFailureDefinitionSaga(),
	watchCopyFailureDefinitionSaga(),
	watchListAllFailureDefinitionsSaga(),
	watchGetFailureDefinitionSaga(),
	watchGetFailureDefinitionRunResultsSaga(),
	watchUpdateFailureDefinitionPublishedStatusSaga(),
	watchRenameFailureDefinitionSaga(),
];

export default failureDefinitionSagas;
